import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isMobileWidth, screenSize } from '@app/helpers';

import './_TooltipLarge.scss';

interface Props {
    bodyCopy: string;
    header?: string;
    children: ReactNode;
    dropdownClosed: boolean;
    directionUp?: boolean;
}

export default function TooltipLarge({ bodyCopy, header, children, dropdownClosed, directionUp }: Props) {
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const [tooltipRightPos, setTooltipRightPos] = useState<string>('-146.5px'); //half of width from spec to center
    const hideTooltipTimeout = useRef<NodeJS.Timeout | null>(null);
    const [userInteraction, setUserInteraction] = useState(false);

    const getTooltipPos = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
        const tooltipWidth = 293; // as per specs
        const screenWidth = screenSize();

        const mouseX = e.clientX;

        if (screenWidth - mouseX - 30 < tooltipWidth / 2) {
            // added 30 for some padding in case
            isMobileWidth() ? setTooltipRightPos('0') : setTooltipRightPos('-25px'); // to align with the dropdown padding
        }
    }, []);

    useEffect(() => {
        if (dropdownClosed) {
            setShowTooltip(false);
        }
    }, [dropdownClosed]);

    const toggleTooltip = useCallback(
        (e: React.MouseEvent<HTMLDivElement>, show: boolean) => {
            e.stopPropagation();
            getTooltipPos(e);
            setShowTooltip(show);
        },
        [getTooltipPos],
    );

    const showClass = useMemo(() => {
        return showTooltip ? 'tooltip__content--show' : userInteraction ? 'tooltip__content--hide' : '';
    }, [showTooltip, userInteraction]);

    const directionUpClass = directionUp ? ' direction-up' : '';

    const Children = () => (
        <>
            {children}

            <div className={`tooltip__content ${showClass}${directionUpClass}`} style={{ right: tooltipRightPos }}>
                {header && <h4 className="tooltip__header">{header}</h4>}
                <p className="tooltip__body-copy">{bodyCopy}</p>
            </div>
        </>
    );

    return (
        <>
            {/*DESKTOP*/}
            <div
                className="tooltip tooltip--desktop"
                onMouseEnter={(e) => {
                    const timeout = hideTooltipTimeout.current;
                    if (timeout) {
                        clearTimeout(timeout);
                    }
                    toggleTooltip(e, true);
                    setUserInteraction(true);
                }}
                onMouseLeave={(e) => {
                    hideTooltipTimeout.current = setTimeout(() => {
                        toggleTooltip(e, false);
                        setUserInteraction(false);
                    }, 200);
                }}
            >
                <Children />
            </div>
            {/*MOBILE*/}
            <div
                className="tooltip tooltip--mobile"
                onClick={(e) => {
                    e.stopPropagation();
                    toggleTooltip(e, !showTooltip);
                }}
            >
                <Children />
            </div>
        </>
    );
}
